import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { Outcome } from '../../types/outcomes';

interface DeleteOutcomeDialogProps {
  outcome: Outcome | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteOutcomeDialog: React.FC<DeleteOutcomeDialogProps> = ({
  outcome,
  isOpen,
  onClose,
  onConfirm,
}) => {
  if (!isOpen || !outcome) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="bg-coral-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Delete Outcome?</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-[var(--bg-raised)]/20 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-charcoal-700 mb-4">
            Are you sure you want to delete <strong>"{outcome.title}"</strong>?
          </p>
          <div className="bg-coral-50 border-2 border-coral-200 p-4">
            <p className="text-sm text-coral-900 font-semibold mb-2">
              This action cannot be undone!
            </p>
            <ul className="text-sm text-coral-800 space-y-1">
              <li>• All progress data will be lost</li>
              <li>• Framework applications will be removed</li>
              <li>• Milestones will be deleted</li>
              <li>• Activity history will be erased</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--rule)] bg-[var(--surface)] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border-2 border-[var(--rule)] text-charcoal-700 hover:bg-[var(--hover-tint)] font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-6 py-2 bg-coral-600 text-white hover:bg-coral-700 font-medium transition-colors flex items-center gap-2"
          >
            <AlertTriangle className="h-5 w-5" />
            Delete Outcome
          </button>
        </div>
      </div>
    </div>
  );
};

